import { createSlice } from '@reduxjs/toolkit'

// Holds ONLY the live round/game-state machine driven by the dynamic
// `${gameCode}/${gameMode}` game-state event. Every other server->client
// event (bets, broadcasts, command replies, settlement) is handled in the
// socket listeners and is intentionally NOT stored here.
//
// Each phase reducer rebuilds `round` from this clean base and sets ONLY the
// fields that phase carries — so a field never lingers into a phase where it
// has no meaning (e.g. `duration` is null during RUNNING, `acceptBetEndTime`
// is null once betting closes).
const initialRound = {
  roundId: null,
  status: null, // SCHEDULED | ACCEPT_BET | STARTING | RUNNING | ENDED | UNDER_MAINTENANCE
  multiplier: null, // grows during RUNNING; final crash point on ENDED
  isCrashed: false,
  duration: null, // ms countdown the server attaches as `delay` (SCHEDULED / ACCEPT_BET / STARTING / ENDED)
  acceptBetEndTime: null, // epoch ms deadline to place/cancel bets (ACCEPT_BET only)
  totalBets: null, // participant count; set on STARTING, kept through RUNNING, cleared on ENDED
  maintenance: null, // { gameCode, gameMode, reason } when UNDER_MAINTENANCE
  // Provably-fair commitment for THIS round: SHA256 of the round's own server
  // seed, published the moment the round is created — before betting opens. Public
  // by design. The seed itself is secret until this round ends and is never present
  // on any live-round event.
  serverSeedHash: null,
}

const initialState = {
  round: { ...initialRound },
  lastCrashMultipliers: [],
}

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    setScheduled: (state, action) => {
      const { roundId, status, delay, serverSeedHash } = action.payload || {}
      // a new round begins — start from a clean slate
      state.round = {
        ...initialRound,
        roundId: roundId ?? null,
        status,
        duration: delay ?? null,
        serverSeedHash: serverSeedHash ?? null,
      }
    },
    setAcceptBet: (state, action) => {
      const { roundId, status, delay, acceptBetEndTime, serverSeedHash } = action.payload || {}
      state.round = {
        ...initialRound,
        roundId: roundId ?? state.round.roundId,
        status,
        duration: delay ?? null,
        acceptBetEndTime: acceptBetEndTime ?? null,
        serverSeedHash: serverSeedHash ?? state.round.serverSeedHash,
      }
    },
    setStarting: (state, action) => {
      const { roundId, status, delay, totalBets, serverSeedHash } = action.payload || {}
      state.round = {
        ...initialRound,
        roundId: roundId ?? state.round.roundId,
        status,
        duration: delay ?? null,
        totalBets: totalBets ?? null,
        serverSeedHash: serverSeedHash ?? state.round.serverSeedHash,
      }
    },
    setRunning: (state, action) => {
      const { roundId, status, multiplier, isCrashed } = action.payload || {}
      state.round = {
        ...initialRound,
        roundId: roundId ?? state.round.roundId,
        status,
        multiplier: multiplier ?? null,
        isCrashed: isCrashed ?? false,
        // carried over from STARTING so the participant count stays visible
        // through the run; cleared on ENDED.
        totalBets: state.round.totalBets ?? null,
        // The 100ms RUNNING tick omits the commitment (it cannot change within a
        // round), so it is carried forward rather than dropped.
        serverSeedHash: state.round.serverSeedHash,
      }
    },
    setEnded: (state, action) => {
      const { roundId, status, multiplier, isCrashed, delay } = action.payload || {}
      state.round = {
        ...initialRound,
        roundId: roundId ?? state.round.roundId,
        status,
        multiplier: multiplier ?? null,
        isCrashed: isCrashed ?? true,
        duration: delay ?? null,
        serverSeedHash: state.round.serverSeedHash,
      }
    },
    setMaintenance: (state, action) => {
      const { gameCode, gameMode, status, reason } = action.payload || {}
      state.round = {
        ...initialRound,
        status,
        maintenance: { gameCode, gameMode, reason: reason ?? null },
      }
    },
    resetRound: () => initialState,
    setLastCrashMultiplier: (state, action) => {
      const { lastCrashMultipliers } = action.payload || {}
      state.lastCrashMultipliers = Array.isArray(lastCrashMultipliers)
        ? [...lastCrashMultipliers]
        : []
    },
  },
})

export const {
  setScheduled,
  setAcceptBet,
  setStarting,
  setRunning,
  setEnded,
  setMaintenance,
  resetRound,
  setLastCrashMultiplier,
} = eventsSlice.actions

export default eventsSlice.reducer
